import React, { FC, useCallback } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";

import UsersSelect from "@app/component/UsersSelect";
import { IWizardStep, StandardStep } from "@app/component/wizard";
import { isCurrentUserOwner } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";

import { getTeamsToShareWith, getUsersToShareWith } from "../../store";

import { AddResourcesWizardState } from ".";
import styles from "./AddResourcesWizard.module.scss";

export interface ShareResourcesStepProps extends IWizardStep<AddResourcesWizardState> {
  isCurrentUserOwner: boolean;
  teams: Team[];
  users: User[];
}

const ShareResourcesStep: FC<ShareResourcesStepProps> = ({
  isCurrentUserOwner,
  onFinish,
  onSetState,
  state: { files, linkName },
  teams,
  users,
  ...props
}) => {
  const intl = useIntl();

  const handleDoNotShare = useCallback(() => {
    onSetState({ userIds: [] });
    onFinish();
  }, [onSetState, onFinish]);

  const handleApply = useCallback(
    (userIds: User["id"][]) => {
      onSetState({ userIds });
      onFinish();
    },
    [onSetState, onFinish]
  );

  return (
    <StandardStep
      {...props}
      className={styles.standardStep}
      footer={null}
      onCancel={handleDoNotShare}
      title={intl.formatMessage(
        {
          id: "resources.add.wizard.resources.share.title",
          defaultMessage: "Share {count, select, 1 {“{name}”} other {{count} resources}}",
          description: "Share resources modal title",
        },
        {
          count: linkName ? 1 : files.length,
          name: files.length === 1 ? files[0].name : linkName ?? "",
        }
      )}
    >
      <UsersSelect
        allUsersWhenEmpty={false}
        cancelText={intl.formatMessage({
          id: "resources.add.wizard.resources.noNotShare",
          defaultMessage: "Do not share",
          description: "Do not share button",
        })}
        confirmText={intl.formatMessage({
          id: "resources.add.wizard.resources.share.confirm",
          defaultMessage: "Share",
          description: "Share confirm button",
        })}
        isCurrentUserOwner={isCurrentUserOwner}
        onApply={handleApply}
        onCancel={handleDoNotShare}
        teams={teams}
        users={users}
      />
    </StandardStep>
  );
};

const mapStateToProps = (state: RootState) => ({
  isCurrentUserOwner: isCurrentUserOwner(state),
  teams: getTeamsToShareWith(state),
  users: getUsersToShareWith(state),
});

export default connect(mapStateToProps)(ShareResourcesStep);
